import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, LoaderCircle } from "lucide-react";
import { api } from "./api";
import type { PageId } from "./types";
import { Sidebar } from "./components/Sidebar";
import { Topbar } from "./components/Topbar";
import { OverviewPage } from "./pages/OverviewPage";
import { MaterialsPage } from "./pages/MaterialsPage";
import { ReviewsPage } from "./pages/ReviewsPage";
import { AdaptersPage } from "./pages/AdaptersPage";

export default function App() {
  const queryClient = useQueryClient();
  const [activePage, setActivePage] = useState<PageId>("overview");
  const [searchValue, setSearchValue] = useState("");
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const dashboardQuery = useQuery({
    queryKey: ["dashboard"],
    queryFn: api.getDashboard,
  });
  const configQuery = useQuery({
    queryKey: ["config"],
    queryFn: api.getConfig,
  });

  function navigate(page: PageId) {
    if (page === "materials") {
      void queryClient.invalidateQueries({ queryKey: ["product"] });
    }
    setActivePage(page);
    setSearchValue("");
  }

  function openProduct(productId: string) {
    setSelectedProductId(productId);
    navigate("materials");
  }

  return (
    <div className="app-shell">
      <Sidebar activePage={activePage} mode={configQuery.data?.mode} onNavigate={navigate} />
      <section className="app-stage">
        <Topbar
          page={activePage}
          generatedAt={dashboardQuery.data?.generated_at ?? null}
          isRefreshing={dashboardQuery.isFetching}
          onRefresh={() => void dashboardQuery.refetch()}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        />
        <main className="page-content">
          {dashboardQuery.isLoading && (
            <div className="state-panel full-state">
              <LoaderCircle className="is-spinning" size={26} aria-hidden="true" />
              <strong>正在连接本地工作流</strong>
              <p>读取商品表、素材包和审核状态。</p>
            </div>
          )}
          {dashboardQuery.isError && (
            <div className="state-panel full-state is-error">
              <AlertTriangle size={26} aria-hidden="true" />
              <strong>无法读取工作流数据</strong>
              <p>{dashboardQuery.error.message}</p>
              <button
                type="button"
                className="secondary-button"
                onClick={() => void dashboardQuery.refetch()}
              >
                重新连接
              </button>
            </div>
          )}
          {dashboardQuery.data && activePage === "overview" && (
            <OverviewPage
              dashboard={dashboardQuery.data}
              onOpenProduct={openProduct}
              onOpenReviews={() => navigate("reviews")}
            />
          )}
          {dashboardQuery.data && activePage === "materials" && (
            <MaterialsPage
              products={dashboardQuery.data.products}
              searchValue={searchValue}
              initialProductId={selectedProductId}
            />
          )}
          {dashboardQuery.data && activePage === "reviews" && (
            <ReviewsPage searchValue={searchValue} />
          )}
          {dashboardQuery.data && activePage === "adapters" && <AdaptersPage />}
        </main>
      </section>
    </div>
  );
}
